import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'urql';
import { DownloadCloud, FileText, Upload } from 'lucide-react';
import PageHeader from '@/components/common/PageHeader.jsx';
import Field from '@/components/common/Field.jsx';
import StickyActions from '@/components/common/StickyActions.jsx';
import ProveedorSelect from './components/ProveedorSelect.jsx';
import ImportesPanel from './components/ImportesPanel.jsx';
import ItemsTable from './components/ItemsTable.jsx';
import OcrDrawer from './components/OcrDrawer.jsx';
import { purchaseFormActions, resetPurchaseFormState, usePurchaseForm } from '@/state/forms/purchaseFormSlice.js';
import { M_CREATE_PURCHASE } from '@/graphql/purchases.gql.js';

const actions = purchaseFormActions();

const tipoOptions = ['Factura A', 'Factura B', 'Factura C', 'Factura M', 'Nota de Crédito', 'Nota de Débito'];
const monedaOptions = ['ARS', 'USD'];
const condicionOptions = ['Contado', 'Cuenta Corriente'];

export default function NuevaCompra() {
  const navigate = useNavigate();
  const state = usePurchaseForm(current => current);
  const [ocrOpen, setOcrOpen] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [, createPurchase] = useMutation(M_CREATE_PURCHASE);

  useEffect(() => {
    resetPurchaseFormState();
  }, []);

  const attachmentsFiles = useMemo(() => state.attachments.map(item => item.file).filter(Boolean), [state.attachments]);

  const handleSubmit = async () => {
    setSubmitError(null);
    if (!state.supplier?.id) {
      setSubmitError('Seleccioná un proveedor registrado para guardar la compra.');
      return;
    }
    const payload = {
      supplierId: state.supplier.id,
      tipo: state.comprobante.tipo,
      puntoVenta: state.comprobante.puntoVenta,
      numero: state.comprobante.numero,
      fecha: state.comprobante.fecha,
      cae: state.comprobante.cae,
      caeVto: state.comprobante.caeVto,
      moneda: state.comprobante.moneda,
      condicionCompra: state.comprobante.condicionCompra,
      importes: {
        neto: Number(state.importes.neto) || 0,
        iva21: Number(state.importes.iva21) || 0,
        iva105: Number(state.importes.iva105) || 0,
        iva27: Number(state.importes.iva27) || 0,
        exento: Number(state.importes.exento) || 0,
        noGravado: Number(state.importes.noGravado) || 0,
        percepIva: Number(state.importes.percepIva) || 0,
        percepIibb: Number(state.importes.percepIibb) || 0,
        otros: Number(state.importes.otros) || 0,
        total: Number(state.importes.total) || 0,
      },
      items: state.items.map(item => ({
        id: item.id,
        codigo: item.codigo,
        descripcion: item.descripcion,
        cantidad: Number(item.cantidad) || 0,
        precio: Number(item.precio) || 0,
        alicuotaIva: Number(item.alicuotaIva) || 0,
        subtotal: Number(item.subtotal) || 0,
      })),
      adjuntos: state.attachments.map(file => ({
        id: file.id,
        filename: file.name,
        mimeType: file.type,
        size: file.size,
      })),
      observaciones: state.comprobante.observaciones,
    };

    const result = await createPurchase({ input: payload });
    if (result.error) {
      setSubmitError(result.error.message || 'No pudimos guardar la compra.');
      return;
    }
    navigate('/tesoreria/compras', { replace: true });
  };

  const handleFileChange = event => {
    const files = Array.from(event.target.files || []);
    if (files.length) {
      actions.addAttachments(files);
    }
    event.target.value = '';
  };

  const handleRemoveAttachment = id => {
    actions.removeAttachment(id);
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Registrar compra"
        description="Cargá el comprobante recibido y mantené la tesorería al día."
        breadcrumbs={[
          { label: 'Tesorería', href: '/tesoreria' },
          { label: 'Registro de compras', href: '/tesoreria/compras' },
          { label: 'Nueva compra' },
        ]}
        actions={
          <button
            type="button"
            onClick={() => setOcrOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-[rgba(var(--brand),0.12)] px-4 py-2 text-sm font-semibold text-[rgb(var(--brand))] shadow-[0_12px_22px_rgba(var(--shadow),0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            <DownloadCloud className="h-4 w-4" aria-hidden="true" />
            Leer con OCR
          </button>
        }
      >
        <span className="rounded-full bg-[rgba(var(--panel),0.55)] px-3 py-1 text-xs font-semibold text-[rgb(var(--brand))]">
          Flujo asistido con IA
        </span>
      </PageHeader>

      <section className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.75)] p-6 shadow-[0_20px_40px_rgba(var(--shadow),0.14)]">
        <ProveedorSelect />
      </section>

      <section className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.75)] p-6 shadow-[0_20px_40px_rgba(var(--shadow),0.14)]">
        <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Datos del comprobante</h2>
        <div className="mt-4 grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-3">
            <Field label="Tipo">
              <select
                value={state.comprobante.tipo}
                onChange={event => actions.setComprobanteField('tipo', event.target.value)}
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                {tipoOptions.map(option => (
                  <option key={option} value={option} className="bg-[rgb(var(--bg))] text-[rgb(var(--fg))]">
                    {option}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="col-span-6 md:col-span-3">
            <Field label="Punto de venta">
              <input
                type="text"
                value={state.comprobante.puntoVenta}
                onChange={event => actions.setComprobanteField('puntoVenta', event.target.value)}
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
          <div className="col-span-6 md:col-span-3">
            <Field label="Número">
              <input
                type="text"
                value={state.comprobante.numero}
                onChange={event => actions.setComprobanteField('numero', event.target.value)}
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
          <div className="col-span-6 md:col-span-3">
            <Field label="Fecha">
              <input
                type="date"
                value={state.comprobante.fecha}
                onChange={event => actions.setComprobanteField('fecha', event.target.value)}
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
          <div className="col-span-6 md:col-span-3">
            <Field label="CAE">
              <input
                type="text"
                value={state.comprobante.cae}
                onChange={event => actions.setComprobanteField('cae', event.target.value)}
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
          <div className="col-span-6 md:col-span-3">
            <Field label="Vencimiento CAE">
              <input
                type="date"
                value={state.comprobante.caeVto}
                onChange={event => actions.setComprobanteField('caeVto', event.target.value)}
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
          <div className="col-span-6 md:col-span-3">
            <Field label="Moneda">
              <select
                value={state.comprobante.moneda}
                onChange={event => actions.setComprobanteField('moneda', event.target.value)}
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                {monedaOptions.map(option => (
                  <option key={option} value={option} className="bg-[rgb(var(--bg))] text-[rgb(var(--fg))]">
                    {option}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="col-span-6 md:col-span-3">
            <Field label="Condición de compra">
              <select
                value={state.comprobante.condicionCompra}
                onChange={event => actions.setComprobanteField('condicionCompra', event.target.value)}
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                {condicionOptions.map(option => (
                  <option key={option} value={option} className="bg-[rgb(var(--bg))] text-[rgb(var(--fg))]">
                    {option}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="col-span-12">
            <Field label="Observaciones">
              <textarea
                rows={3}
                value={state.comprobante.observaciones}
                onChange={event => actions.setObservaciones(event.target.value)}
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
        </div>
      </section>

      <ImportesPanel />

      <ItemsTable />

      <section className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.75)] p-6 shadow-[0_20px_40px_rgba(var(--shadow),0.14)]">
        <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Adjuntos</h2>
        <p className="mt-1 text-sm text-[rgba(var(--muted),0.85)]">Podés sumar el PDF o imagen del comprobante para auditoría.</p>
        <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-center">
          <label className="flex items-center gap-3 rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.55)] px-4 py-3 text-sm font-semibold text-[rgb(var(--fg))] shadow-[0_12px_22px_rgba(var(--shadow),0.1)] focus-within:ring-2 focus-within:ring-[rgb(var(--ring))]">
            <Upload className="h-4 w-4 text-[rgb(var(--brand))]" aria-hidden="true" />
            <span>Seleccionar archivos</span>
            <input
              type="file"
              multiple
              accept="application/pdf,image/*"
              onChange={handleFileChange}
              className="sr-only"
            />
          </label>
          {attachmentsFiles.length ? (
            <button
              type="button"
              onClick={() => setOcrOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-[rgba(var(--brand),0.35)] bg-[rgba(var(--brand),0.12)] px-4 py-2 text-sm font-semibold text-[rgb(var(--brand))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              <FileText className="h-4 w-4" aria-hidden="true" />
              Usar estos archivos en OCR
            </button>
          ) : null}
        </div>
        {state.attachments.length ? (
          <ul className="mt-4 space-y-3">
            {state.attachments.map(file => (
              <li
                key={file.id}
                className="flex items-center justify-between rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.5)] px-4 py-3 text-sm text-[rgb(var(--fg))]"
              >
                <div>
                  <p className="font-semibold">{file.name}</p>
                  <p className="text-xs text-[rgba(var(--muted),0.8)]">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveAttachment(file.id)}
                  className="rounded-xl border border-[rgba(var(--border),0.4)] bg-[rgba(var(--panel),0.6)] px-3 py-1 text-xs font-semibold text-[rgba(var(--muted),0.85)] hover:text-[rgb(var(--brand))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                >
                  Quitar
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-4 text-sm text-[rgba(var(--muted),0.8)]">Aún no adjuntaste archivos.</p>
        )}
      </section>

      {submitError ? (
        <div className="rounded-2xl border border-[rgba(var(--brand),0.45)] bg-[rgba(var(--brand),0.1)] px-4 py-3 text-sm text-[rgb(var(--brand-700))]">
          {submitError}
        </div>
      ) : null}

      <StickyActions
        onCancel={() => navigate('/tesoreria/compras')}
        onSubmit={handleSubmit}
        primaryLabel="Guardar compra"
        secondaryLabel="Cancelar"
      />

      <OcrDrawer isOpen={ocrOpen} onClose={() => setOcrOpen(false)} files={attachmentsFiles} />
    </div>
  );
}
